import React from "react";
import { motion, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { ArrowDown, Sparkles, Star } from "lucide-react";

const MASCOT =
  "https://customer-assets.emergentagent.com/job_learning-nova-grow/artifacts/qqk955vd_WhatsApp_Image_2026-05-10_at_19.59.57-removebg-preview.png";

export default function Hero() {
  const reduceMotion = useReducedMotion();

  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const tiltXSpring = useSpring(tiltX, { stiffness: 180, damping: 18, mass: 0.6 });
  const tiltYSpring = useSpring(tiltY, { stiffness: 180, damping: 18, mass: 0.6 });

  const onMascotMove = (e) => {
    if (reduceMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const dx = px - 0.5;
    const dy = py - 0.5;

    tiltX.set(-dy * 10);
    tiltY.set(dx * 12);
  };

  const onMascotLeave = () => {
    tiltX.set(0);
    tiltY.set(0);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 md:pt-40 pb-16 sm:pb-20 md:pb-28 px-5 sm:px-6 md:px-12 lg:px-16 overflow-hidden grain"
      style={{
        background:
          "radial-gradient(60% 60% at 80% 10%, #FDF3E7 0%, transparent 60%), radial-gradient(40% 40% at 10% 80%, #F3F7F0 0%, transparent 60%), #FDFBF7",
      }}
    >
      <div className="max-w-[1400px] mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex items-center gap-2 mb-5 sm:mb-7"
        >
          <span className="text-[10px] sm:text-[11px] tracking-[0.25em] uppercase font-bold text-[#FF6B6B]">
            ✦ Tuition  · Grade 1 to 12
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* LEFT — headline */}
          <div className="lg:col-span-8 order-2 lg:order-1">
            <h1 className="font-display font-black text-[#0B132B] text-[13vw] sm:text-[11vw] md:text-[9.5vw] lg:text-[8vw] xl:text-[7.5vw] leading-[0.88] tracking-tighter">
              <motion.span
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.05 }}
                className="block"
              >
                Grow up
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.18 }}
                className="block"
              >
                <motion.span
                  className="italic font-script font-medium text-[#FF6B6B] mr-2 sm:mr-3 inline-block cursor-grab active:cursor-grabbing"
                  drag
                  dragElastic={0.2}
                  dragMomentum={false}
                  whileTap={{ scale: 0.98 }}
                  aria-label="Brilliantly"
                >
                  brilliantly
                </motion.span>
                with us.
              </motion.span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-6 sm:mt-8 text-base sm:text-lg md:text-xl text-[#3A506B] leading-relaxed max-w-xl"
              data-testid="hero-subtitle"
            >
              <span className="font-display font-bold text-[#0B132B]">LearnWithNova</span> guides every student
              from their very first chalk-and-board day in 1<sup>st</sup> standard all the way up to the 12<sup>th</sup>
              board exams — with mentors who actually care.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <a
                href="https://wa.me/917012708655"
                data-testid="hero-enroll-btn"
                className="btn-pill bg-[#FF6B6B] hover:bg-[#FF4F4F] text-white shadow-[0_12px_30px_rgba(255,107,107,0.35)] justify-center sm:justify-start"
                target="_blank"
                rel="noreferrer"
              >
                Contact Us Now
              </a>
              <a
                href="#journey"
                data-testid="hero-journey-btn"
                className="btn-pill border border-[#0B132B]/15 text-[#0B132B] hover:bg-[#0B132B] hover:text-white justify-center sm:justify-start"
              >
                About us <ArrowDown size={16} />
              </a>
            </motion.div>
          </div>

          {/* RIGHT — mascot */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 26, rotate: -2 }}
            animate={{ opacity: 1, scale: 1, y: 0, rotate: 0 }}
            transition={{ duration: 0.9, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4 order-1 lg:order-2 relative flex justify-center lg:justify-end"
            data-testid="hero-mascot-wrap"
          >
            {/* Decorative blobs */}
            <span
              className="absolute -z-0 w-[70%] aspect-square rounded-full blur-3xl opacity-50"
              style={{ background: "#FDF3E7", top: "8%", right: "8%" }}
              aria-hidden="true"
            />
            <span
              className="absolute -z-0 w-[40%] aspect-square rounded-full blur-3xl opacity-60"
              style={{ background: "#F3F7F0", bottom: "5%", left: "5%" }}
              aria-hidden="true"
            />

            <div
              className="relative z-10 w-[62%] sm:w-[48%] md:w-[42%] lg:w-full max-w-[420px]"
              style={{ perspective: 1000 }}
              onPointerMove={onMascotMove}
              onPointerLeave={onMascotLeave}
            >
              {/* breathing glow */}
              <motion.span
                aria-hidden="true"
                className="absolute -inset-8 rounded-full blur-3xl"
                style={{ background: "#FDF3E7" }}
                animate={
                  reduceMotion
                    ? { opacity: 0.45, scale: 1 }
                    : { opacity: [0.35, 0.55, 0.35], scale: [0.98, 1.06, 0.98] }
                }
                transition={
                  reduceMotion
                    ? { duration: 0.01 }
                    : { duration: 3.8, repeat: Infinity, ease: "easeInOut" }
                }
              />

              <motion.div
                style={{ rotateX: tiltXSpring, rotateY: tiltYSpring, transformStyle: "preserve-3d" }}
                whileHover={reduceMotion ? undefined : { scale: 1.02 }}
                whileTap={reduceMotion ? undefined : { scale: 0.99 }}
                animate={
                  reduceMotion
                    ? { y: 0, rotate: 0 }
                    : {
                        y: [0, -10, 0],
                        rotate: [0, -0.8, 0.8, 0],
                      }
                }
                transition={
                  reduceMotion
                    ? { duration: 0.01 }
                    : { duration: 4.4, repeat: Infinity, ease: "easeInOut" }
                }
                className="relative"
              >
                <motion.img
                  src={MASCOT}
                  alt="LearnWithNova student mascot"
                  data-testid="hero-mascot-image"
                  className="w-full h-auto select-none pointer-events-none"
                  draggable={false}
                  initial={{ filter: "blur(10px)", opacity: 0 }}
                  animate={{ filter: "blur(0px)", opacity: 1 }}
                  transition={{ duration: 0.75, delay: 0.35, ease: "easeOut" }}
                />
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Stat strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          className="mt-16 sm:mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-3 gap-y-6 sm:gap-y-8 gap-x-6 border-t border-[#0B132B]/10 pt-8 sm:pt-10"
        >
          {[
            { k: "12", l: "grades covered" },
            { k: "9", l: "parent rating" },
            { k: "5yrs", l: "of nurturing growth" },
          ].map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <div className="font-display text-3xl sm:text-4xl md:text-5xl font-black text-[#0B132B] tracking-tighter">
                {s.k}
              </div>
              <div className="text-xs sm:text-sm text-[#758A9F] mt-1 flex items-center gap-1">
                <Star size={12} className="text-[#FCA311]" fill="#FCA311" />
                {s.l}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
